import { FC, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTable } from "spacetimedb/react";
import { tables } from "@module-bindings";
import type { Market, OrderbookDepth } from "@module-bindings/types";
import { assetIdKey, fixQuery, formatAssetLabel, formatAssetValue } from "@/lib/marketUtils";
import type { AssetId } from "@module-bindings/types";
import { TradingPaneState } from "./TradingPaneState";

type ViewMode = "both" | "bids" | "asks";

const FRAC_BITS = 60n;
const ROW_HEIGHT_PX = 28;
const SECTION_BOTTOM_PADDING_PX = 20;

/** Convert fixed-point u128 price to a display number. */
function priceToNumber(raw: bigint): number {
  const integer = Number(raw >> FRAC_BITS);
  const fractional = Number(raw & ((1n << FRAC_BITS) - 1n)) / Number(1n << FRAC_BITS);
  return integer + fractional;
}

interface PriceLevel {
  price: number;
  quoteAmount: number;
  quoteTotal: number;
}

/** Format orderbook depth into price levels, sorted best-first. */
function buildLevels(depths: readonly OrderbookDepth[], side: "Bid" | "Ask"): PriceLevel[] {
  const byPrice = new Map<number, number>();
  for (const d of depths) {
    const amount = side == "Bid" ? d.bidAmount : d.askAmount;
    if (amount === 0n) continue;
    const p = priceToNumber(d.price);
    byPrice.set(p, Number(amount));
  }

  // Sort: bids high→low, asks low→high
  const sorted = [...byPrice.entries()].sort((a, b) =>
    side === "Bid" ? b[0] - a[0] : a[0] - b[0]
  );

  let cumulative = 0;
  return sorted.map(([price, quoteAmount]) => {
    cumulative += quoteAmount;
    return { price, quoteAmount, quoteTotal: cumulative };
  });
}

interface OrderBookProps {
  selectedMarket: Market | null;
  onSelectPrice?: (price: number) => void;
}

export const OrderBook: FC<OrderBookProps> = ({ selectedMarket, onSelectPrice }) => {
  const [viewMode, setViewMode] = useState<ViewMode>("both");
  const askContainerRef = useRef<HTMLDivElement | null>(null);
  const bidContainerRef = useRef<HTMLDivElement | null>(null);
  const [askContainerHeight, setAskContainerHeight] = useState(0);
  const [bidContainerHeight, setBidContainerHeight] = useState(0);

  // Query by the selected market
  const pairKey = useMemo(() => {
    if (!selectedMarket) return '';
    const baseKey = assetIdKey(selectedMarket.tradingPair.base);
    const quoteKey = assetIdKey(selectedMarket.tradingPair.quote);
    return `${baseKey}/${quoteKey}`;
  }, [selectedMarket])
  const query = fixQuery(tables.orderbook_depth.where(r => r.pairKey.eq(pairKey)), [['pairKey', 'pair_key']]);
  const [depths] = useTable(query);

  const askLevels = useMemo(() => buildLevels(depths, "Ask"), [depths]);
  const bidLevels = useMemo(() => buildLevels(depths, "Bid"), [depths]);

  const quoteLabel = selectedMarket ? formatAssetLabel(selectedMarket.tradingPair.quote) : "Quote";

  const showAsks = viewMode !== "bids";
  const showBids = viewMode !== "asks";
  const showSpread = viewMode === "both";

  useLayoutEffect(() => {
    const observers: ResizeObserver[] = [];

    const observeHeight = (
      element: HTMLDivElement | null,
      onHeightChange: (height: number) => void,
    ) => {
      if (!element) return;

      const measure = () => onHeightChange(element.clientHeight);
      measure();

      const observer = new ResizeObserver(measure);
      observer.observe(element);
      observers.push(observer);
    };

    observeHeight(askContainerRef.current, setAskContainerHeight);
    observeHeight(bidContainerRef.current, setBidContainerHeight);

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [showAsks, showBids, showSpread]);

  const getVisibleRowCount = (
    containerHeight: number,
    fallbackRows: number,
    bottomPadding = 0,
  ) => {
    if (containerHeight <= 0) return fallbackRows;
    return Math.max(1, Math.floor((containerHeight - bottomPadding) / ROW_HEIGHT_PX));
  };

  // Keep the levels nearest the spread and drop the far ends when space is tight.
  const visibleAskLevels = useMemo(() => {
    const fallbackRows = showSpread ? 8 : 16;
    const maxRows = getVisibleRowCount(
      askContainerHeight,
      fallbackRows,
      showSpread ? 0 : SECTION_BOTTOM_PADDING_PX,
    );

    return askLevels.slice(0, maxRows);
  }, [askContainerHeight, askLevels, showSpread]);

  const visibleBidLevels = useMemo(() => {
    const fallbackRows = showSpread ? 8 : 16;
    const maxRows = getVisibleRowCount(
      bidContainerHeight,
      fallbackRows,
      SECTION_BOTTOM_PADDING_PX,
    );

    return bidLevels.slice(0, maxRows);
  }, [bidContainerHeight, bidLevels, showSpread]);

  // Spread
  const bestAsk = askLevels[0]?.price;
  const bestBid = bidLevels[0]?.price;
  const spread = bestAsk != null && bestBid != null ? bestAsk - bestBid : null;
  const spreadPct = spread != null && bestAsk > 0
    ? ((spread / bestAsk) * 100).toFixed(2)
    : null;

  const maxAskTotal = visibleAskLevels.length > 0 ? visibleAskLevels[visibleAskLevels.length - 1].quoteTotal : 1;
  const maxBidTotal = visibleBidLevels.length > 0 ? visibleBidLevels[visibleBidLevels.length - 1].quoteTotal : 1;

  // For "both" mode, show asks reversed so best ask is at the bottom (near spread)
  const displayAsks = showSpread ? [...visibleAskLevels].reverse() : visibleAskLevels;

  if (!selectedMarket) {
    return (
      <div className="sr-panel flex h-full min-h-[20rem] flex-col">
        <TradingPaneState
          eyebrow="Depth"
          title="No market selected"
          message="Pick a trading pair to inspect the order book."
        />
      </div>
    );
  }

  if (askLevels.length === 0 && bidLevels.length === 0) {
    return (
      <div className="sr-panel flex h-full min-h-[20rem] flex-col">
        <div className="sr-panel-header">
          <h3 className="text-[11px] font-medium uppercase tracking-wider text-sr-neutral-300">
            Order Book
          </h3>
        </div>
        <TradingPaneState
          eyebrow="Depth"
          title="No liquidity yet"
          message="This market has no active bids or asks. Check back after the first orders land."
        />
      </div>
    );
  }

  return (
    <section className="sr-panel flex h-full flex-col" aria-label="Order book">
      {/* Header */}
      <div className="sr-panel-header border-none pb-2">
        <h3 className="text-[11px] font-medium text-sr-neutral-300 uppercase tracking-wider">
          Order Book
        </h3>
        <div className="flex items-center gap-0.5" role="toolbar" aria-label="Order book view mode">
          <button
            onClick={() => setViewMode("both")}
            className="sr-focus-ring rounded p-1"
            title="Both"
            aria-pressed={viewMode === "both"}
            aria-label="Show asks and bids"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="2" y="2" width="5" height="5" rx="1" fill={viewMode === "both" ? "var(--color-sr-salmon)" : "var(--color-sr-neutral-700)"} />
              <rect x="9" y="2" width="5" height="5" rx="1" fill={viewMode === "both" ? "var(--color-sr-salmon)" : "var(--color-sr-neutral-700)"} />
              <rect x="2" y="9" width="5" height="5" rx="1" fill={viewMode === "both" ? "var(--color-sr-mint)" : "var(--color-sr-neutral-700)"} />
              <rect x="9" y="9" width="5" height="5" rx="1" fill={viewMode === "both" ? "var(--color-sr-mint)" : "var(--color-sr-neutral-700)"} />
            </svg>
          </button>
          <button
            onClick={() => setViewMode("bids")}
            className="sr-focus-ring rounded p-1"
            title="Bids"
            aria-pressed={viewMode === "bids"}
            aria-label="Show bids only"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="2" y="2" width="5" height="5" rx="1" fill={viewMode === "bids" ? "var(--color-sr-mint)" : "var(--color-sr-neutral-700)"} />
              <rect x="9" y="2" width="5" height="5" rx="1" fill={viewMode === "bids" ? "var(--color-sr-mint)" : "var(--color-sr-neutral-700)"} />
              <rect x="2" y="9" width="5" height="5" rx="1" fill={viewMode === "bids" ? "var(--color-sr-mint)" : "var(--color-sr-neutral-700)"} />
              <rect x="9" y="9" width="5" height="5" rx="1" fill={viewMode === "bids" ? "var(--color-sr-mint)" : "var(--color-sr-neutral-700)"} />
            </svg>
          </button>
          <button
            onClick={() => setViewMode("asks")}
            className="sr-focus-ring rounded p-1"
            title="Asks"
            aria-pressed={viewMode === "asks"}
            aria-label="Show asks only"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="2" y="2" width="5" height="5" rx="1" fill={viewMode === "asks" ? "var(--color-sr-salmon)" : "var(--color-sr-neutral-700)"} />
              <rect x="9" y="2" width="5" height="5" rx="1" fill={viewMode === "asks" ? "var(--color-sr-salmon)" : "var(--color-sr-neutral-700)"} />
              <rect x="2" y="9" width="5" height="5" rx="1" fill={viewMode === "asks" ? "var(--color-sr-salmon)" : "var(--color-sr-neutral-700)"} />
              <rect x="9" y="9" width="5" height="5" rx="1" fill={viewMode === "asks" ? "var(--color-sr-salmon)" : "var(--color-sr-neutral-700)"} />
            </svg>
          </button>
        </div>
      </div>

      {/* Column headers */}
      <div className="grid grid-cols-3 items-end text-[9px] text-sr-neutral-500 uppercase tracking-wider px-2 pb-1">
        <span className="px-2">Price</span>
        <span className="text-right">Amount ({quoteLabel})</span>
        <span className="text-right px-2">Total ({quoteLabel})</span>
      </div>

      {/* Asks */}
      {showAsks && (
        <div
          ref={askContainerRef}
          className={`flex flex-col overflow-hidden ${
            showSpread ? "flex-1 justify-end" : "flex-1 pb-5"
          }`}
        >
          {displayAsks.length > 0 ? displayAsks.map((level, i) => (
            <Row key={`ask-${i}`} level={level} side="ask" maxTotal={maxAskTotal} quoteAsset={selectedMarket?.tradingPair.quote ?? null} onSelect={onSelectPrice} />
          )) : (
            <div className="flex-1 flex items-end justify-center pb-2">
              <span className="text-xs text-sr-neutral-700">No asks</span>
            </div>
          )}
        </div>
      )}

      {/* Spread */}
      {showSpread && (
        <div className="flex h-7 items-center justify-between border-y border-sr-border-subtle/50 px-3 text-[10px]">
          <span className="text-sr-neutral-500">Spread</span>
          {spread != null ? (
            <div className="flex items-center gap-2">
              <span className="text-sr-neutral-300 tabular-nums">{spreadPct}%</span>
              <span className="text-white tabular-nums font-display">{spread.toFixed(6)} {quoteLabel}</span>
            </div>
          ) : (
            <span className="text-sr-neutral-700">—</span>
          )}
        </div>
      )}

      {/* Bids */}
      {showBids && (
        <div ref={bidContainerRef} className="flex flex-1 flex-col overflow-hidden pb-5">
          {visibleBidLevels.length > 0 ? visibleBidLevels.map((level, i) => (
            <Row key={`bid-${i}`} level={level} side="bid" maxTotal={maxBidTotal} quoteAsset={selectedMarket?.tradingPair.quote ?? null} onSelect={onSelectPrice} />
          )) : (
            <div className="flex-1 flex items-start justify-center pt-2">
              <span className="text-xs text-sr-neutral-700">No bids</span>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

const Row: FC<{
  level: PriceLevel;
  side: "ask" | "bid";
  maxTotal: number;
  quoteAsset: AssetId | null;
  onSelect?: (price: number) => void;
}> = ({ level, side, maxTotal, quoteAsset, onSelect }) => {
  const depthPct = maxTotal > 0 ? (level.quoteTotal / maxTotal) * 100 : 0;
  const isAsk = side === "ask";
  const formatQuote = (value: number) => {
    if (!quoteAsset) {
      return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }

    return formatAssetValue(quoteAsset, BigInt(Math.round(value)));
  };

  return (
    <div
      className="relative grid h-7 shrink-0 grid-cols-3 items-center px-2 text-[10px] hover:bg-white/5 cursor-pointer"
      onClick={() => onSelect?.(level.price)}
    >
      <div
        className={`absolute inset-y-0 right-0 pointer-events-none ${isAsk ? "bg-sr-salmon/20" : "bg-sr-mint/15"}`}
        style={{ width: `${depthPct}%` }}
      />
      <span className={`relative tabular-nums font-display px-1 ${isAsk ? "text-sr-salmon" : "text-sr-mint"}`}>
        {level.price.toFixed(6)}
      </span>
      <span className="relative text-right text-white tabular-nums">
        {formatQuote(level.quoteAmount)}
      </span>
      <span className="relative text-right text-white tabular-nums px-1">
        {formatQuote(level.quoteTotal)}
      </span>
    </div>
  );
};
